import { FirebaseError, getApp, getApps, initializeApp } from "firebase/app"
import { GoogleAuthProvider, getAuth, linkWithCredential, PhoneAuthProvider, RecaptchaVerifier, signInWithPopup, updatePhoneNumber } from "firebase/auth"
import ky from "ky"
import { z } from "zod"
import { firebaseProjectForWeb } from "../../../../packages/config/src/firebase-environments.ts"
import { isNativeApp, NativeCapabilityError, requestNativeCapability } from "./native-capabilities.ts"
import { ensureNativePhoneIdentity, invalidateNativePushRegistration } from "./native-session-client.ts"
import { type PhoneCredentialLinkCompletionTransport, type PhoneCredentialLinkReason, phoneCredentialLinkCompletionTransportSchema } from "./todo26-contract.ts"

declare global {
  namespace NodeJS {
    interface ProcessEnv {
      readonly NEXT_PUBLIC_API_BASE_URL?: string
      readonly NEXT_PUBLIC_FIREBASE_API_KEY?: string
      readonly NEXT_PUBLIC_FIREBASE_APP_ID?: string
      readonly NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN?: string
      readonly NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID?: string
      readonly NEXT_PUBLIC_FIREBASE_PROJECT_ID?: string
    }
  }
}

const loginResponseSchema = z.strictObject({ nextRoute: z.string().startsWith("/"), ok: z.literal(true) })

const firebaseConfiguration = () => {
  const configuration = z.strictObject({ apiKey: z.string().min(1), appId: z.string().min(1), authDomain: z.string().min(1), messagingSenderId: z.string().min(1), projectId: z.string().min(1) }).parse({
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  })
  if (firebaseProjectForWeb({ ...configuration, apiBase: process.env.NEXT_PUBLIC_API_BASE_URL ?? "/api", webOrigin: window.location.origin }) === null) throw new FirebaseLoginError("unavailable")
  return configuration
}

export class FirebaseLoginError extends Error {
  override readonly name = "FirebaseLoginError"
  constructor(readonly code: "cancelled" | "failed" | "unavailable" | "update-required") {
    super(code)
  }
}

export class FirebasePhoneLinkError extends Error {
  override readonly name = "FirebasePhoneLinkError"
  constructor(readonly code: "current-user-required" | "verification-failed") {
    super(code)
  }
}

const auth = () => {
  const app = getApps().length === 0 ? initializeApp(firebaseConfiguration()) : getApp()
  return getAuth(app)
}

export const loginWithFirebaseGoogle = async (identityProvider: "google" | "apple" = "google"): Promise<string> => {
  try {
    let idToken: string
    if (isNativeApp()) {
      const result = await requestNativeCapability("signIn", { provider: identityProvider })
      idToken = z.string().min(1).parse(result["idToken"])
    } else {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ prompt: "select_account" })
      const credential = await signInWithPopup(auth(), provider)
      idToken = await credential.user.getIdToken(true)
    }
    const response: unknown = await ky.post(`${process.env.NEXT_PUBLIC_API_BASE_URL ?? "/api"}/identity/login`, { credentials: "include", json: { client: "WEB", idToken }, retry: 0, timeout: 15_000 }).json()
    const next = loginResponseSchema.parse(response).nextRoute
    invalidateNativePushRegistration()
    window.dispatchEvent(new Event("cashhub:session-ready"))
    return next
  } catch (error: unknown) {
    if (error instanceof NativeCapabilityError) throw new FirebaseLoginError(error.code === "CANCELLED" ? "cancelled" : error.code === "UNSUPPORTED" ? "update-required" : error.code === "UNAVAILABLE" ? "unavailable" : "failed")
    const firebaseError = z.object({ code: z.string() }).safeParse(error)
    if ((error instanceof FirebaseError || firebaseError.success) && firebaseError.success && (firebaseError.data.code === "auth/popup-closed-by-user" || firebaseError.data.code === "auth/cancelled-popup-request")) throw new FirebaseLoginError("cancelled")
    if (error instanceof z.ZodError) throw new FirebaseLoginError("unavailable")
    if (error instanceof FirebaseLoginError) throw error
    throw new FirebaseLoginError("failed")
  }
}

const authenticatedUser = async () => {
  const currentAuth = auth()
  await currentAuth.authStateReady()
  if (currentAuth.currentUser === null) throw new FirebasePhoneLinkError("current-user-required")
  return { currentAuth, user: currentAuth.currentUser }
}

export const requestCurrentUserPhoneVerification = async ({ containerId, phoneNumber }: { readonly containerId: string; readonly phoneNumber: string }): Promise<string> => {
  if (isNativeApp()) {
    let firebaseUid: string | null
    try {
      firebaseUid = await ensureNativePhoneIdentity()
    } catch {
      throw new FirebasePhoneLinkError("current-user-required")
    }
    const result = await requestNativeCapability("requestPhone", { phoneNumber, ...(firebaseUid === null ? {} : { firebaseUid }) })
    return z.string().min(1).parse(result["verificationId"])
  }
  const { currentAuth } = await authenticatedUser()
  const verifier = new RecaptchaVerifier(currentAuth, containerId, { size: "invisible" })
  try {
    return await new PhoneAuthProvider(currentAuth).verifyPhoneNumber(phoneNumber, verifier)
  } catch {
    throw new FirebasePhoneLinkError("verification-failed")
  } finally {
    verifier.clear()
  }
}

export const linkCurrentUserPhoneCredential = async ({ linkReason, returnTo, verificationCode, verificationId }: { readonly linkReason: PhoneCredentialLinkReason; readonly returnTo: string; readonly verificationCode: string; readonly verificationId: string }): Promise<PhoneCredentialLinkCompletionTransport> => {
  if (isNativeApp()) {
    let firebaseUid: string | null
    try {
      firebaseUid = await ensureNativePhoneIdentity()
    } catch {
      throw new FirebasePhoneLinkError("current-user-required")
    }
    const result = await requestNativeCapability("completePhone", { linkReason, verificationCode, verificationId, ...(firebaseUid === null ? {} : { firebaseUid }) })
    return phoneCredentialLinkCompletionTransportSchema.parse({ firebaseIdToken: result["firebaseIdToken"], linkReason, returnTo, tokenRefresh: "FORCE_REFRESHED" })
  }
  const { user } = await authenticatedUser()
  const credential = PhoneAuthProvider.credential(verificationId, verificationCode)
  try {
    if (linkReason === "INITIAL_VERIFICATION") await linkWithCredential(user, credential)
    else await updatePhoneNumber(user, credential)
    const firebaseIdToken = await user.getIdToken(true)
    return phoneCredentialLinkCompletionTransportSchema.parse({ firebaseIdToken, linkReason, returnTo, tokenRefresh: "FORCE_REFRESHED" })
  } catch (error: unknown) {
    if (error instanceof FirebasePhoneLinkError) throw error
    throw new FirebasePhoneLinkError("verification-failed")
  }
}
